/**
 * EmailHtmlModal — open one email from a conversation thread and render it as
 * the recipient actually saw it.
 *
 * WHY THIS EXISTS: the conversation thread stores only a `subject: …` stub as a
 * message body. The real email — HTML, images, signature — was never persisted
 * anywhere in Layer; it lives in the Gmail mailbox. So a bubble on `/pr/people/:id`
 * or `/members/:id` could tell you an email happened and what it was called, but
 * not what it said. This closes that gap without copying correspondence bodies
 * into Postgres: Gmail stays the system of record and we fetch on demand.
 *
 * It reads through the canonical `read_gmail` action over the normal
 * `POST /api/dispatch/:action` chokepoint — NOT a bespoke endpoint. A dedicated
 * `/api/gmail-message/:id` route would duplicate an abstraction that already
 * exists, and would need its own actor + scope enforcement rather than
 * inheriting dispatch's.
 *
 * TWO THINGS THAT LOOK LIKE DETAILS AND ARE NOT:
 *
 * 1. The body renders inside a `sandbox=""` iframe, never `dangerouslySetInnerHTML`.
 *    Inbound mail is attacker-controlled HTML from outside the company — a
 *    journalist's reply, or anyone who emails the studio. An empty sandbox
 *    blocks scripts, form submission, and same-origin access, so the worst a
 *    hostile email can do is look strange. Images still load, which is why the
 *    real email renders properly rather than as bare text. There is no HTML
 *    sanitizer in the dashboard's dependencies and this needs none.
 *
 * 2. Every open mints a FRESH `request_id` (dispatchAction's default). `read_gmail`
 *    declares `body_html` in `responsePiiPaths`, so an idempotency REPLAY returns
 *    the same shape with the body nulled (invariant 11). Reusing a stable
 *    request_id keyed on the message would make the first open work and every
 *    subsequent one render an empty email.
 */

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { dispatchAction } from "../lib/dispatch";
import { studioFormat } from "../lib/studio-tz";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/**
 * Render the RFC-2822 `Date` header in the STUDIO's timezone, like every other
 * timestamp on the dashboard. Gmail returns the header as the sender wrote it
 * (a real example carried `-0500`), so showing it raw would put a foreign
 * offset next to thread bubbles that are all in studio time — two clocks on one
 * screen. Falls back to the raw header if it will not parse, since an
 * unparseable date is still information.
 */
function fmtEmailDate(raw: string): string {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : studioFormat(DATETIME_OPTS).format(d);
}

/** The subset of `read_gmail`'s message shape this viewer renders. */
interface GmailMessage {
  readonly subject: string | null;
  readonly from: string | null;
  readonly to: string | null;
  readonly date: string | null;
  readonly body_text: string | null;
  readonly body_html: string | null;
}

interface ReadGmailResponse {
  readonly messages: ReadonlyArray<GmailMessage>;
}

/** `read_resend_email`'s wire shape (the fields this viewer renders). */
interface ReadResendEmailResponse {
  readonly message: {
    readonly subject: string | null;
    readonly from: string | null;
    readonly to: ReadonlyArray<string> | null;
    readonly created_at: string;
    readonly body_text: string | null;
    readonly body_html: string | null;
  };
}


export interface EmailHtmlModalProps {
  /** Gmail message id (the thread row's `vendor_message_id`). */
  readonly messageId: string;
  readonly onClose: () => void;
}

/** Re-exported for convenience; the rule itself lives in the dependency-light
 *  `lib/email-thread` so tests can import it without React or supabase. */
export { isOpenableEmail } from "../lib/email-thread";
import { isResendMessageId } from "../lib/email-thread";

/**
 * Wrap a plain-text body so it renders legibly in the same iframe as an HTML
 * one. Escaped, because text/plain that happens to contain markup must not
 * become live markup on the way through.
 */
function textToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<pre style="white-space:pre-wrap;word-wrap:break-word;margin:0">${escaped}</pre>`;
}

/**
 * The document handed to the iframe. A `<base target="_blank">` makes any link
 * in the email open in a new tab instead of navigating the sandboxed frame to a
 * blank page, and the wrapper gives the message the white page background it was
 * designed against regardless of the dashboard's theme.
 */
function buildSrcDoc(html: string): string {
  return [
    "<!doctype html><html><head><meta charset=\"utf-8\">",
    '<base target="_blank">',
    "<style>html,body{margin:0;padding:16px;background:#fff;color:#111;",
    "font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif}",
    "img{max-width:100%;height:auto}</style></head><body>",
    html,
    "</body></html>",
  ].join("");
}

export function EmailHtmlModal({
  messageId,
  onClose,
}: EmailHtmlModalProps): JSX.Element {
  const [message, setMessage] = useState<GmailMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Escape closes, like every other modal on the dashboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        // Route by id namespace: a dashed UUID lives at Resend (the hello@
        // rail), bare hex lives in the Gmail mailbox. 2026-07-22 — before
        // `read_resend_email` existed, every tracked send dead-ended here.
        if (isResendMessageId(messageId)) {
          const r = await dispatchAction<
            { message_id: string },
            ReadResendEmailResponse
          >("read_resend_email", { message_id: messageId });
          if (cancelled) return;
          if (!r.ok) {
            setError(
              r.code === "resend_message_not_found"
                ? "Resend no longer has this message, so there's no full copy to show."
                : `Couldn't load the email (${r.code ?? "unknown"}): ${r.message ?? ""}`,
            );
            return;
          }
          const m = r.response.message;
          setMessage({
            subject: m.subject,
            from: m.from,
            to: m.to === null ? null : m.to.join(", "),
            date: m.created_at,
            body_text: m.body_text,
            body_html: m.body_html,
          });
          return;
        }
        const r = await dispatchAction<
          { message_id: string; format: string },
          ReadGmailResponse
        >("read_gmail", { message_id: messageId, format: "full" });
        if (cancelled) return;
        if (!r.ok) {
          // Say WHICH failure this is: a Gmail-id message genuinely gone from
          // the mailbox. (Resend uuids no longer reach this branch — they
          // route to read_resend_email above.)
          //
          // 2026-08-06 — this line used to be a LIE on `/inbox`. read_gmail
          // read only gmail.impersonate_user (contact@), Gmail ids are
          // per-mailbox, and every operator-inbox message lives in one of
          // Mario's own mailboxes — so a message sitting right there in Gmail
          // reported itself missing. The action now searches every declared
          // mailbox, which is what makes the sentence true (and plural).
          setError(
            r.code === "gmail_api_call_failed" || r.code === "not_found"
              ? "This message isn't in any of the studio's Gmail mailboxes, so there's no full copy to show."
              : `Couldn't load the email (${r.code ?? "unknown"}): ${r.message ?? ""}`,
          );
          return;
        }
        const first = r.response.messages[0];
        if (first === undefined) {
          setError("Gmail returned no message for that id.");
          return;
        }
        setMessage(first);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messageId]);

  const onBackdrop = useCallback(
    (e: React.MouseEvent): void => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const html =
    message === null
      ? null
      : message.body_html !== null && message.body_html.length > 0
        ? message.body_html
        : message.body_text !== null && message.body_text.length > 0
          ? textToHtml(message.body_text)
          : null;

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={onBackdrop}>
      <div
        className="modal-card email-modal-card"
        role="dialog"
        aria-modal="true"
        aria-label="Email"
      >
        <h3 className="modal-title">
          {message?.subject ?? (loading ? "Loading email…" : "Email")}
        </h3>
        {message !== null ? (
          <p className="modal-subtitle email-modal-headers">
            {message.from !== null ? <span>From {message.from}</span> : null}
            {message.to !== null ? <span> · To {message.to}</span> : null}
            {message.date !== null ? (
              <span> · {fmtEmailDate(message.date)}</span>
            ) : null}
          </p>
        ) : null}

        {loading ? <p className="dc-muted">Fetching from Gmail…</p> : null}
        {error !== null ? <p className="action-modal-error">{error}</p> : null}

        {html !== null ? (
          <iframe
            className="email-modal-frame"
            title={message?.subject ?? "Email"}
            sandbox=""
            srcDoc={buildSrcDoc(html)}
          />
        ) : !loading && error === null ? (
          <p className="dc-muted">This email has no readable body.</p>
        ) : null}

        <div className="modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
